import * as z from 'zod';

import OutcomeSchema from './OutcomeSchema';
import StageSchema from './StageSchema';
import TableRowColorsSchema from './TableRowColorsSchema';

const OverviewTeamSchema = z.object({
  no: z.number(),
  leagueName: z.string(),
  playedMatches: z.number(),
  wins: z.number(),
  draws: z.number(),
  losses: z.number(),
  points: z.number(),
});

const OverviewSchema = z.object({
  type: z.literal('ihs-overview'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: OverviewTeamSchema,
    away: OverviewTeamSchema,
  }),
});

const PreviousMeetingsSchema = z.object({
  type: z.literal('ihs-previous-meetings'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    homeWins: z.number(),
    draws: z.number(),
    awayWins: z.number(),
  }),
});

const VictoryProbabilitySchema = z.object({
  type: z.literal('ihs-victory-probability'),
  data: z
    .object({
      homeTeam: z.string(),
      awayTeam: z.string(),
      home: z.number(),
      draw: z.number(),
      away: z.number(),
    })
    .nullable(),
});

const LastMatchSchema = z.object({
  homeTeam: z.string(),
  awayTeam: z.string(),
  homeScore: z.number(),
  awayScore: z.number(),
  homePenaltyScore: z.number().nullable(),
  awayPenaltyScore: z.number().nullable(),
  outcome: OutcomeSchema,
  homeGoalscorers: z.array(z.string()),
  awayGoalscorers: z.array(z.string()),
  date: z.string(),
  league: z.string(),
  leagueFlag: z.string().nullable(),
  stage: StageSchema.nullable(),
  stageNumber: z.number().nullable(),
});

const LastFiveGamesSchema = z.object({
  type: z.literal('ihs-last-5-games'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    homeMatches: z.array(LastMatchSchema),
    awayMatches: z.array(LastMatchSchema),
  }),
});

const GoalscorerSchema = z.object({
  name: z.string(),
  goals: z.number(),
  position: z.string().nullable(),
  flag: z.string().nullable(),
});

const TopScorersSchema = z.object({
  type: z.literal('ihs-top-scorers'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: z.array(GoalscorerSchema),
    away: z.array(GoalscorerSchema),
  }),
});

const SeasonTeamStatsSchema = z.object({
  gamesPlayed: z.number(),
  winRate: z.number(),
  possession: z.number(),
});

const SeasonStatsSchema = z.object({
  type: z.literal('ihs-season-stats'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: SeasonTeamStatsSchema,
    away: SeasonTeamStatsSchema,
  }),
});

const TeamGoalsSchema = z.object({
  totalGoals: z.number(),
  goalsScored: z.number(),
  goalsConceded: z.number(),
});

const GoalsSchema = z.object({
  type: z.literal('ihs-goals'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: TeamGoalsSchema,
    away: TeamGoalsSchema,
  }),
});

const TeamOverGoalsSchema = z.object({
  over15Goals: z.number(),
  over25Goals: z.number(),
  over05GoalsHT: z.number(),
  over15GoalsHT: z.number(),
});

const OverGoalsSchema = z.object({
  type: z.literal('ihs-over-goals'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: TeamOverGoalsSchema,
    away: TeamOverGoalsSchema,
  }),
});

const TeamShotsSchema = z.object({
  shotsPerGame: z.number(),
  shotsOnTarget: z.number(),
  shotsOffTarget: z.number(),
  shotsConcededOnTarget: z.number(),
  shotsConcededOffTarget: z.number(),
});

const ShotsSchema = z.object({
  type: z.literal('ihs-shots'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: TeamShotsSchema,
    away: TeamShotsSchema,
  }),
});

const TeamAverageMatchStatsSchema = z.object({
  yellowCards: z.number(),
  redCards: z.number(),
  corners: z.number(),
  freeKicks: z.number(),
  fouls: z.number(),
});

const AverageMatchStatsSchema = z.object({
  type: z.literal('ihs-avg-match-stats'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: TeamAverageMatchStatsSchema,
    away: TeamAverageMatchStatsSchema,
  }),
});

const VenueRefereeSchema = z.object({
  type: z.literal('ihs-venue-referee'),
  data: z.object({
    venue: z.object({
      name: z.string(),
      capacity: z.number().nullable(),
    }),
    referee: z
      .object({
        name: z.string(),
        flag: z.string().nullable(),
      })
      .nullable(),
  }),
});

const PopularOddsSchema = z.object({
  type: z.literal('popular-odds'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    betTypes: z.array(z.object({ criterion_id: z.string(), line: z.number().nullable() })),
  }),
});

const LastFiveGamesCompactSchema = LastFiveGamesSchema.extend({
  type: z.literal('ihs-last-5-games-compact'),
});

const LeagueTableRowSchema = z.object({
  no: z.number(),
  teamName: z.string(),
  leagueName: z.string(),
  playedMatches: z.number(),
  wins: z.number(),
  draws: z.number(),
  losses: z.number(),
  points: z.number(),
  form: z.array(OutcomeSchema),
  qualification: z.string().nullable(),
  color: TableRowColorsSchema.nullable(),
  inPlay: z.boolean(),
  jersey: z.string().nullable(),
  flag: z.null(),
});

const ColorLegendItemSchema = z.object({
  color: TableRowColorsSchema,
  label: z.string(),
  description: z.string(),
});

const LeagueTableSchema = z.object({
  type: z.literal('ihs-league-table'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    leagueName: z.string(),
    totalTeams: z.number(),
    tableRows: z.array(LeagueTableRowSchema),
    colorLegend: z.array(ColorLegendItemSchema),
  }),
});

const PlayerPositionSchema = z.enum(['forward', 'defender', 'midfielder', 'goalkeeper']);

const BasePlayerDataSchema = z.object({
  name: z.string(),
  position: PlayerPositionSchema.nullable(),
  flag: z.string().nullable(),
});

const GoalsPlayerDataSchema = BasePlayerDataSchema.extend({
  goals: z.number(),
});

const AssistsPlayerDataSchema = BasePlayerDataSchema.extend({
  assists: z.number(),
});

const ShotsPlayerDataSchema = BasePlayerDataSchema.extend({
  shotsOnTarget: z.number(),
  shotsOffTarget: z.number(),
});

const CardsPlayerDataSchema = BasePlayerDataSchema.extend({
  yellowCards: z.number(),
  redCards: z.number(),
});

const InjuriesPlayerDataSchema = BasePlayerDataSchema.extend({
  status: z.enum(['missing', 'doubtful']),
});

const TeamPlayerStatsSchema = z.object({
  goals: z.array(GoalsPlayerDataSchema),
  assists: z.array(AssistsPlayerDataSchema),
  shots: z.array(ShotsPlayerDataSchema),
  cards: z.array(CardsPlayerDataSchema),
  injuries: z.array(InjuriesPlayerDataSchema),
});

const PlayerStatsSchema = z.object({
  type: z.literal('ihs-player-stats'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    home: TeamPlayerStatsSchema,
    away: TeamPlayerStatsSchema,
  }),
});

const LineupPlayerSchema = z.object({
  name: z.string(),
  goals: z.number(),
  assists: z.number(),
});

const LineupTeamSchema = z.object({
  lines: z.array(z.array(LineupPlayerSchema)),
  bench: z.array(LineupPlayerSchema),
});

// TODO: based on a fixture, can change later
const LineupSchema = z.object({
  type: z.literal('ihs-line-up'),
  data: z.object({
    home: LineupTeamSchema,
    away: LineupTeamSchema,
  }),
});

const UpcomingMatchSchema = z.object({
  homeTeam: z.string(),
  awayTeam: z.string(),
  date: z.string(),
  league: z.string(),
});

// TODO: based on a fixture, can change later
const MatchRowUpcomingSchema = z.object({
  type: z.literal('match-row-upcoming'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    homeMatches: z.array(UpcomingMatchSchema),
    awayMatches: z.array(UpcomingMatchSchema),
  }),
});

const FinishedMatchSchema = z.object({
  homeTeam: z.string(),
  awayTeam: z.string(),
  homeScore: z.number(),
  awayScore: z.number(),
  homeGoalscorers: z.array(z.string()),
  awayGoalscorers: z.array(z.string()),
});

const MatchRowFinishedSchema = z.object({
  type: z.literal('match-row-finished'),
  data: z.object({
    homeTeam: z.string(),
    awayTeam: z.string(),
    homeMatches: z.array(FinishedMatchSchema),
    awayMatches: z.array(FinishedMatchSchema),
  }),
});

const WidgetsSchema = z.object({
  widgets: z.array(
    z.discriminatedUnion('type', [
      OverviewSchema,
      PreviousMeetingsSchema,
      VictoryProbabilitySchema,
      LastFiveGamesSchema,
      TopScorersSchema,
      SeasonStatsSchema,
      GoalsSchema,
      OverGoalsSchema,
      ShotsSchema,
      AverageMatchStatsSchema,
      VenueRefereeSchema,
      PopularOddsSchema,
      LastFiveGamesCompactSchema,
      LeagueTableSchema,
      PlayerStatsSchema,
      LineupSchema,
      MatchRowUpcomingSchema,
      MatchRowFinishedSchema,
    ]),
  ),
});

export default WidgetsSchema;
